// ============================================================
// Pay Rocky — Live Rates Page
// ============================================================

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { PublicLayout, PageContainer, SectionHeading } from '../components/layout/Layout';
import { Button, Card } from '../components/ui/Primitives';
import { useRates } from '../context/RatesContext';
import { CRYPTO_LIST } from '../data/constants';
import CryptoLogo from '../components/CryptoLogo';
import { formatNGN } from '../utils/helpers';

export default function RatesPage() {
  const navigate = useNavigate();
  const { rates, lastUpdated, fetching, fetchRates, usdToNgn } = useRates();

  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '900px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '2rem' }}>
          <SectionHeading
            tag="Live Rates"
            title="Today's crypto rates"
            subtitle="Rates are locked for 15 minutes on order creation. NGN/USD midmarket rate used for calculation."
          />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
            <Button variant="ghost" size="sm" onClick={() => fetchRates(true)} loading={fetching}>
              <RefreshCw size={13} />
              Refresh
            </Button>
            {lastUpdated && (
              <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>
                Updated {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </div>

        {/* NGN/USD rate */}
        <div style={{ background: 'var(--clr-accent-dim)', border: '1px solid var(--clr-accent-glow)', borderRadius: 'var(--radius-md)', padding: '10px 16px', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: 'var(--clr-accent)', fontWeight: 600 }}>NGN / USD midmarket rate</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '15px', fontWeight: 700, color: 'var(--clr-accent)' }}>
            ₦{usdToNgn.toLocaleString()}
          </span>
        </div>

        {/* Rate table */}
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          {/* Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr 1fr',
            padding: '10px 20px',
            background: 'var(--clr-surface-2)',
            borderBottom: '1px solid var(--clr-border)',
            fontSize: '11px',
            fontWeight: 600,
            color: 'var(--clr-text-3)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            <span>Asset</span>
            <span style={{ textAlign: 'right' }}>USD price</span>
            <span style={{ textAlign: 'right' }}>Buy rate (NGN)</span>
            <span style={{ textAlign: 'right' }}>Sell rate (NGN)</span>
          </div>

          {CRYPTO_LIST.map((crypto, i) => {
            const rate = rates[crypto.id];
            return (
              <div key={crypto.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                padding: '16px 20px',
                borderBottom: i < CRYPTO_LIST.length - 1 ? '1px solid var(--clr-border)' : 'none',
                alignItems: 'center',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CryptoLogo cryptoId={crypto.id} size={38} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '14px' }}>{crypto.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--clr-text-3)' }}>{crypto.symbol} · {crypto.networks.join(', ')}</div>
                  </div>
                </div>

                <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--clr-text-2)' }}>
                  {rate ? `$${rate.usdPrice.toLocaleString()}` : '—'}
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '14px', fontWeight: 600, color: 'var(--clr-accent)' }}>
                    {rate ? formatNGN(rate.buy) : '—'}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '14px', fontWeight: 600, color: 'var(--clr-danger)' }}>
                    {rate ? formatNGN(rate.sell) : '—'}
                  </div>
                </div>
              </div>
            );
          })}
        </Card>

        {/* CTA */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '2rem', justifyContent: 'center' }}>
          <Button variant="primary" size="lg" onClick={() => navigate('/buy')}>Buy Crypto</Button>
          <Button variant="ghost" size="lg" onClick={() => navigate('/sell')}>Sell Crypto</Button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '12px', color: 'var(--clr-text-3)' }}>
          Rates are for reference only and may change before order placement. A 15-minute rate lock is applied when you create an order.
        </div>
      </PageContainer>
    </PublicLayout>
  );
}
